import { useEffect, useState } from "react";
import { StringParam, useQueryParams } from "use-query-params";
import type { TaskView } from "@common/types/views/tasksView";
import type { TasksLanding } from "@components/tasks/tasksLanding";
import {
    ORGANIZATION_USER_CACHE_KEYS,
    useOrganizationUserCache,
} from "@hooks/useOrganizationUserCache";
import { useUserStatus } from "@hooks/useUserStatus";
import { useOrganizationTasksListFiltersCache } from "@hooks/view/useTasksListFiltersCache";
import { resolveClientTasksLink } from "./organizationTasksLanding";

/**
 * Applies a `?clientId=` link (see `getOrganizationClientTasksLink`): saves
 * the filters for that one client (and its `closeId`, when linked) and the
 * base view, the linked `view` or none, then drops the link's params from the
 * address bar.
 * The page shows its loader while `isApplyingClientLink`, so its content
 * mounts afterwards with `clientLanding` and reads the saved state. Display
 * preferences are left as they are unless the link names a view.
 */
export function useClientTasksLink() {
    const [{ clientId, view: linkedViewId, closeId }, setQueryParams] =
        useQueryParams({
            clientId: StringParam,
            view: StringParam,
            closeId: StringParam,
        });
    const { data: userStatus } = useUserStatus();
    const [, setCachedFilters] = useOrganizationTasksListFiltersCache();
    const [, setCachedBaseViewId] = useOrganizationUserCache<string | null>(
        ORGANIZATION_USER_CACHE_KEYS.CLIENTS_TASKS_PAGE_BASE_VIEW_ID_CACHE_KEY,
    );
    const [, setCachedView] = useOrganizationUserCache<TaskView>(
        ORGANIZATION_USER_CACHE_KEYS.CLIENTS_TASKS_PAGE_VIEW_CACHE_KEY,
    );
    const [clientLanding, setClientLanding] = useState<TasksLanding | null>(
        null,
    );
    const isUserLoaded = userStatus?.user.id !== undefined;

    useEffect(() => {
        if (!clientId || !isUserLoaded) {
            return;
        }
        const { landing, view } = resolveClientTasksLink({
            clientId,
            viewId: linkedViewId,
            closeId,
        });
        setCachedFilters(landing.defaultQuery);
        setCachedBaseViewId(landing.landingViewId ?? null);
        if (view) {
            setCachedView(view);
        }
        setClientLanding(landing);
        setQueryParams(
            { clientId: undefined, view: undefined, closeId: undefined },
            "replaceIn",
        );
    }, [
        clientId,
        linkedViewId,
        closeId,
        isUserLoaded,
        setCachedFilters,
        setCachedBaseViewId,
        setCachedView,
        setQueryParams,
    ]);

    return { isApplyingClientLink: Boolean(clientId), clientLanding };
}
